import {
  TextField,
  Grid2 as Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import storeConnector from '@src/store/storeConnector';
import { ISections } from '@src/types/Stacks';
import { useFormContext, Controller } from 'react-hook-form';
import { FormValues } from './TemplatesCard';
import { templatesViewSx } from '@src/components/_shared/sxStyles/Config/templates';

interface TemplatesFieldsProps {
  inputFields: ISections['fields'];
}

function TemplatesFields({ inputFields }: TemplatesFieldsProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();

  return (
    <Grid spacing={2} container>
      {inputFields.map((field, index) => {
        const fieldName = field.controllerField;

        return (
          <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <FormControl
              fullWidth
              key={index}
              sx={templatesViewSx.fields}
              error={!!errors[fieldName]}
            >
              {['string', 'number', 'boolean'].includes(field.type) && (
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    required: `${field.uiLabel} is required`,
                    ...(field.type === 'number' && {
                      min: {
                        value: field.range?.min ?? -Infinity,
                        message: `Minimum value is ${field.range?.min}`,
                      },
                      max: {
                        value: field.range?.max ?? Infinity,
                        message: `Maximum value is ${field.range?.max}`,
                      },
                    }),
                  }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <>
                      <TextField
                        label={field.uiLabel}
                        variant='outlined'
                        size='small'
                        value={value ?? ''}
                        name={name}
                        inputRef={ref}
                        type={field.type === 'string' ? 'text' : 'number'}
                        onChange={(e) => {
                          const val =
                            field.type === 'number'
                              ? e.target.value === ''
                                ? ''
                                : Number(e.target.value)
                              : e.target.value;
                          onChange(val);
                        }}
                        inputProps={{
                          ...(field.type === 'number'
                            ? {
                                min: field.range?.min,
                                max: field.range?.max,
                                step: 'any',
                              }
                            : {}),
                          ...(field.type === 'boolean'
                            ? {
                                min: 0,
                                max: 1,
                                step: 1,
                              }
                            : {}),
                        }}
                        error={!!errors[fieldName]}
                      />
                      {errors[fieldName] && (
                        <FormHelperText>
                          {errors[fieldName]?.message?.toString()}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              )}
              {field.type === 'select' && (
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{ required: `${field.uiLabel} is required` }}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <>
                      <InputLabel id={`select-${fieldName}`}>
                        {field.uiLabel}
                      </InputLabel>
                      <Select
                        label={field.uiLabel}
                        labelId={`select-${fieldName}`}
                        variant='outlined'
                        size='small'
                        value={value ?? ''}
                        name={name}
                        inputRef={ref}
                        onChange={onChange}
                        error={!!errors[fieldName]}
                      >
                        {field.listOptions?.map(
                          (option: string, index: number) => (
                            <MenuItem key={index} value={option}>
                              {option}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      {errors[fieldName] && (
                        <FormHelperText>
                          {errors[fieldName]?.message?.toString()}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              )}
            </FormControl>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default storeConnector(TemplatesFields, {
  config: ['siteMeta'],
});
